import { useState, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import useGsapReveal from '../useGsapReveal';
import { sectionStyles, getCardClass, getAnimation, hexToRgba, buildSrcSet, getImageSizes, getGridCols, getCardRadius, getCardGap } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import MediaRenderer, { resolveItemMedia } from './MediaRenderer';

const MemberSocials = ({item, accent}) => {
    const links = [
        item.facebook && {url: item.facebook, icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z'},
        item.instagram && {url: item.instagram, icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z'},
        item.linkedin && {url: item.linkedin, icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'},
        item.twitter && {url: item.twitter, icon: 'M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z'},
    ].filter(Boolean);
    if (links.length === 0) return null;
    return (
        <div className="flex items-center gap-2 mt-2">
            {links.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:scale-110" style={{backgroundColor: hexToRgba(accent, 0.08)}}>
                    <svg className="w-3.5 h-3.5" style={{color: accent}} fill="currentColor" viewBox="0 0 24 24"><path d={l.icon}/></svg>
                </a>
            ))}
        </div>
    );
};

export default function TeamSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const mediaMode = config.media_mode || 'image';
    const imageFit = config.image_fit || 'cover';
    const fitClass = imageFit === 'contain' ? 'object-contain' : imageFit === 'auto' ? 'object-scale-down' : 'object-cover';
    const carouselInterval = (config.carousel_interval || 4) * 1000;
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const gsapRef = useGsapReveal({ animation: 'stagger-children', disabled: !enableAnimations });
    const variant = config.variant || 'grid';

    // Resolve photos from mediaFolders + media mode
    const items = rawItems.map(item => {
        const media = resolveItemMedia(item, mediaMode, mediaFolders);
        return { ...item, ...media, photoUrl: media.resolvedImage };
    });

    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    if (items.length === 0) return null;

    const visibleItems = items.slice(0, visibleCount);

    const LoadMoreBtn = () => visibleCount < items.length ? (
        <div className="text-center mt-8">
            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                style={{borderColor: s.accent, color: s.accent}}>
                Ver mas ({items.length - visibleCount} restantes)
            </button>
        </div>
    ) : null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const columns = config.columns || 3;
    const cols = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    // Image config
    const imgShape = config.image_shape || 'circle';
    const imgSize = config.image_size || 'md';
    const imgPosition = config.image_position || 'center';
    const SHAPE_CLS = { circle: 'rounded-full', rounded: 'rounded-xl', square: 'rounded-none' };
    const SIZE_CLS = { sm: 'w-16 h-16', md: 'w-20 h-20', lg: 'w-28 h-28', xl: 'w-36 h-36' };
    const SIZE_TEXT = { sm: 'text-xl', md: 'text-3xl', lg: 'text-4xl', xl: 'text-5xl' };
    const POS_CLS = { left: 'justify-start', center: 'justify-center', right: 'justify-end' };

    // Avatar: photo or fallback (emoji/initial)
    const Avatar = ({item, size, textSize, rounded}) => {
        const sz = size || SIZE_CLS[imgSize];
        const ts = textSize || SIZE_TEXT[imgSize];
        const rd = rounded || SHAPE_CLS[imgShape];
        return item.photoUrl ? (
            <img src={item.photoUrl} alt={item.name || ''} className={`${sz} ${rd} object-cover`} loading="lazy" decoding="async" srcSet={buildSrcSet(item)} sizes={getImageSizes(columns)}/>
        ) : (
            <div className={`${sz} ${rd} flex items-center justify-center ${ts}`}
                style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                {item.icon || item.name?.charAt(0) || '?'}
            </div>
        );
    }

    // ── Photo cards (image on top) ──
    if (variant === 'photo-cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="aspect-[4/5] overflow-hidden">
                                    {(item.resolvedImage || item.carouselImages || item.video) ? (
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[4/5]" rounded="" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-7xl" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                            {item.icon || item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                </div>
                                <div className="p-5 text-center">
                                    <h3 className="text-base font-bold" style={{ color: s.text }}>{item.name}</h3>
                                    {item.role && <p className="text-sm font-medium mt-1" style={{ color: s.accent }}>{item.role}</p>}
                                    {item.description && <p className="text-xs leading-relaxed mt-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="flex justify-center"><MemberSocials item={item} accent={s.accent}/></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Photo circle (circular photo centered) ──
    if (variant === 'photo-circle') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-8`}>
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`group text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="mx-auto mb-4 group-hover:scale-105 transition-transform duration-300">
                                    {item.photoUrl ? (
                                        <img src={item.photoUrl} alt={item.name || ''} className="w-28 h-28 rounded-full object-cover mx-auto ring-4 shadow-lg" style={{ '--tw-ring-color': s.dark ? 'rgba(255,255,255,0.15)' : '#ffffff' }} loading="lazy" decoding="async" srcSet={buildSrcSet(item)} sizes={getImageSizes(columns)}/>
                                    ) : (
                                        <div className="w-28 h-28 rounded-full flex items-center justify-center text-4xl mx-auto ring-4 shadow-lg"
                                            style={{ '--tw-ring-color': s.dark ? 'rgba(255,255,255,0.15)' : '#ffffff', backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                                            {item.icon || item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.name}</h3>
                                {item.role && <p className="text-sm font-medium mt-1" style={{ color: s.accent }}>{item.role}</p>}
                                {item.description && <p className="text-sm leading-relaxed mt-2 max-w-xs mx-auto" style={{ color: s.textSub }}>{item.description}</p>}
                                <div className="flex justify-center"><MemberSocials item={item} accent={s.accent}/></div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Photo overlay (photo background with dark overlay) ──
    if (variant === 'photo-overlay') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="aspect-[3/4] relative">
                                    {(item.resolvedImage || item.carouselImages || item.video) ? (
                                        <div className="absolute inset-0"><MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-[3/4]" rounded="" className="group-hover:scale-110 transition-transform duration-700" /></div>
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-8xl" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                            {item.icon || item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
                                    <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                        <h3 className="text-lg font-bold">{item.name}</h3>
                                        {item.role && <p className="text-sm text-white/80 mt-0.5">{item.role}</p>}
                                        {item.description && <p className="text-xs text-white/60 mt-2 line-clamp-2">{item.description}</p>}
                                        <MemberSocials item={item} accent="#ffffff"/>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Hover reveal variant ──
    if (variant === 'hover-reveal') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-2 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="aspect-square overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-500">
                                    {(item.resolvedImage || item.carouselImages || item.video) ? (
                                        <MediaRenderer item={item} fitClass={fitClass} interval={carouselInterval} accent={s.accent} aspect="aspect-square" rounded="" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-6xl" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                            {item.icon || item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 p-4 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-10">
                                    <h3 className="text-sm font-bold text-white">{item.name}</h3>
                                    {item.role && <p className="text-xs text-white/80 mt-0.5">{item.role}</p>}
                                    <MemberSocials item={item} accent="#ffffff"/>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── List variant ──
    if (variant === 'list') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-4">
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`${card} flex items-center gap-6 p-5 transition-all duration-500 hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="shrink-0">
                                    <Avatar item={item} size="w-16 h-16" textSize="text-2xl"/>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-bold" style={{ color: s.text }}>{item.name}</h3>
                                    {item.role && <p className="text-sm font-medium mt-0.5" style={{ color: s.accent }}>{item.role}</p>}
                                    {item.description && <p className="text-sm leading-relaxed mt-1" style={{ color: s.textSub }}>{item.description}</p>}
                                    <MemberSocials item={item} accent={s.accent}/>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Flip cards variant ──
    if (variant === 'flip-cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {visibleItems.map((item, i) => (
                            <div key={i} className={`group h-80 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ perspective: '1000px', transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <div className="relative w-full h-full transition-transform duration-[600ms] group-hover:[transform:rotateY(180deg)]"
                                    style={{ transformStyle: 'preserve-3d' }}>
                                    {/* Front */}
                                    <div className="absolute inset-0 rounded-2xl shadow-lg overflow-hidden flex flex-col items-center justify-center"
                                        style={{ backfaceVisibility: 'hidden', backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}>
                                        <div className="mb-4">
                                            <Avatar item={item} size="w-24 h-24" textSize="text-4xl" rounded="rounded-full"/>
                                        </div>
                                        <h3 className="text-lg font-bold px-4 text-center" style={{ color: s.text }}>{item.name}</h3>
                                        {item.role && <p className="text-sm font-medium mt-1" style={{ color: s.accent }}>{item.role}</p>}
                                    </div>
                                    {/* Back */}
                                    <div className="absolute inset-0 rounded-2xl shadow-lg overflow-hidden flex flex-col items-center justify-center p-6 text-center"
                                        style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', backgroundColor: s.accent }}>
                                        <h3 className="text-lg font-bold text-white mb-2">{item.name}</h3>
                                        {item.description && <p className="text-sm text-white/80 leading-relaxed mb-4 line-clamp-5">{item.description}</p>}
                                        <MemberSocials item={item} accent="#ffffff"/>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Spotlight variant ──
    if (variant === 'spotlight') {
        const SpotlightCard = ({ item, index }) => {
            const cardRef = useRef(null);
            const handleMouseMove = useCallback((e) => {
                const rect = cardRef.current?.getBoundingClientRect();
                if (!rect) return;
                cardRef.current.style.setProperty('--glow-x', `${e.clientX - rect.left}px`);
                cardRef.current.style.setProperty('--glow-y', `${e.clientY - rect.top}px`);
            }, []);
            return (
                <div ref={cardRef} onMouseMove={handleMouseMove}
                    className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{
                        backgroundColor: s.dark ? 'rgba(255,255,255,0.04)' : '#111827',
                        transitionDelay: visible ? `${Math.min(index, 10) * 100}ms` : '0ms',
                        '--glow-x': '50%', '--glow-y': '50%',
                    }}>
                    {/* Glow overlay */}
                    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ background: `radial-gradient(300px circle at var(--glow-x) var(--glow-y), ${hexToRgba(s.accent, 0.25)}, transparent 60%)` }} />
                    <div className="relative z-10 p-6 text-center">
                        <div className="mb-4 mx-auto w-24 h-24 rounded-2xl overflow-hidden ring-2 transition-all duration-300 group-hover:ring-4"
                            style={{ '--tw-ring-color': hexToRgba(s.accent, 0.3) }}>
                            {item.photoUrl ? (
                                <img src={item.photoUrl} alt={item.name || ''} className="w-full h-full object-cover" loading="lazy" decoding="async" srcSet={buildSrcSet(item)} sizes={getImageSizes(columns)}/>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl" style={{ backgroundColor: hexToRgba(s.accent, 0.12), color: s.accent }}>
                                    {item.icon || item.name?.charAt(0) || '?'}
                                </div>
                            )}
                        </div>
                        <h3 className="text-lg font-bold text-white">{item.name}</h3>
                        {item.role && <p className="text-sm font-medium mt-1" style={{ color: s.accent }}>{item.role}</p>}
                        {item.description && <p className="text-xs text-white/60 leading-relaxed mt-3 line-clamp-3">{item.description}</p>}
                        <div className="flex justify-center"><MemberSocials item={item} accent={s.accent}/></div>
                    </div>
                </div>
            );
        };
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`grid grid-cols-1 ${cols} gap-6`}>
                        {visibleItems.map((item, i) => <SpotlightCard key={i} item={item} index={i} />)}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Masonry variant ──
    if (variant === 'masonry') {
        const masonryCols = columns === 2 ? 2 : columns === 4 ? 4 : 3;
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div style={{ columns: masonryCols, columnGap: '1.5rem' }}>
                        {visibleItems.map((item, i) => {
                            const hasPhoto = !!item.photoUrl;
                            const hasBio = !!item.description;
                            return (
                                <div key={i} className={`${card} overflow-hidden mb-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                    style={{ breakInside: 'avoid', transitionDelay: visible ? `${Math.min(i, 10) * 120}ms` : '0ms' }}>
                                    {hasPhoto && (
                                        <div className={i % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}>
                                            <img src={item.photoUrl} alt={item.name || ''} className={`w-full h-full ${fitClass}`} loading="lazy" decoding="async" srcSet={buildSrcSet(item)} sizes={getImageSizes(columns)}/>
                                        </div>
                                    )}
                                    {!hasPhoto && (
                                        <div className="aspect-square flex items-center justify-center text-6xl"
                                            style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                                            {item.icon || item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <h3 className="text-base font-bold" style={{ color: s.text }}>{item.name}</h3>
                                        {item.role && <p className="text-sm font-medium mt-1" style={{ color: s.accent }}>{item.role}</p>}
                                        {hasBio && <p className="text-sm leading-relaxed mt-2" style={{ color: s.textSub }}>{item.description}</p>}
                                        <div className="flex justify-start"><MemberSocials item={item} accent={s.accent}/></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Grid variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div ref={gsapRef} className={`grid grid-cols-1 ${getGridCols(config) || cols} ${getCardGap(config) || 'gap-6'}`}>
                    {visibleItems.map((item, i) => {
                        const txtAlign = imgPosition === 'left' ? 'text-left' : imgPosition === 'right' ? 'text-right' : 'text-center';
                        const socialJustify = imgPosition === 'left' ? 'justify-start' : imgPosition === 'right' ? 'justify-end' : 'justify-center';
                        return (
                        <div key={i} className={`group ${card} p-6 ${txtAlign} transition-all duration-500 hover:-translate-y-1 ${getCardRadius(config)} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                            <div className={`flex ${POS_CLS[imgPosition]} mb-4 group-hover:scale-105 transition-transform`}>
                                <Avatar item={item}/>
                            </div>
                            <h3 className="text-lg font-bold mb-1" style={{ color: s.text }}>{item.name}</h3>
                            {item.role && <p className="text-sm font-medium mb-2" style={{ color: item.accent_color || s.accent }}>{item.role}</p>}
                            {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                            <div className={`flex ${socialJustify}`}><MemberSocials item={item} accent={item.accent_color || s.accent}/></div>
                        </div>
                        );
                    })}
                </div>
                <LoadMoreBtn />
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
